import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Activity,
  BarChart3,
  ExternalLink,
  LoaderCircle,
  PauseCircle,
  PlayCircle,
  Plus,
  Power,
  RadioTower,
  ReceiptText,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserCheck,
  UserCog,
  Users,
  UserX,
  Wallet,
  Wifi,
} from "lucide-react";
import Footer from "./Footer";
import NavBar from "./NavBar";
import Loading from "./Loading";
import api, { authHeaders } from "../services/api";

function moeda(valor) {
  return `${Number(valor || 0).toLocaleString("pt-MZ")} MZN`;
}

function tipoDoUsuario(usuario) {
  return usuario?.tipoUsuario || usuario?.tipousuario || "";
}

function clienteAtivo(usuario) {
  const disabled = String(usuario?.disabled || "").toLowerCase();
  const profile = String(usuario?.profile || "").toUpperCase();
  const estadosDesativos = ["DESATVADO", "DESATIVADO", "DESATIVO"];
  return disabled !== "true" && !estadosDesativos.includes(profile);
}

function dataCurta(data) {
  if (!data) return "Sem data";
  const parsed = new Date(data);
  if (Number.isNaN(parsed.getTime())) return "Sem data";
  return new Intl.DateTimeFormat("pt-MZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsed);
}

function dataHoraCurta(data) {
  if (!data) return "Sem data";
  const parsed = new Date(data);
  if (Number.isNaN(parsed.getTime())) return "Sem data";
  return new Intl.DateTimeFormat("pt-MZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
}

function mesAtualFiltro() {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, "0");
  return `${agora.getFullYear()}-${mes}`;
}

function detalheLog(log) {
  if (!log?.detalhes) return {};
  if (typeof log.detalhes === "object") return log.detalhes;

  try {
    return JSON.parse(log.detalhes);
  } catch {
    return {};
  }
}

function resumoLog(log) {
  if (log.descricao) return log.descricao;

  const detalhes = detalheLog(log);

  if (detalhes.tipo === "ativacao_extra") {
    const cliente = detalhes.clienteName || "cliente";
    const dias = Number(detalhes.diasConcedidos || 0);
    const plano = detalhes.plano || "plano seleccionado";
    const motivo = detalhes.observacao ? ` Motivo: ${detalhes.observacao}.` : "";

    return `${log.nomeUsuario || "Admin"} activou ${cliente} com ${dias} dia${
      dias === 1 ? "" : "s"
    } extra${dias === 1 ? "" : "s"} no plano ${plano}.${motivo}`;
  }

  return log.acao || "Acao registada no sistema.";
}

function clienteDoLog(log) {
  const detalhes = detalheLog(log);
  return detalhes.clienteName || detalhes.cliente?.name || "N/D";
}

function resultadoDoLog(log) {
  return Number(log.sucesso) === 1 ? "Concluido" : "Falhou";
}

function diasRestantesLabel(cliente) {
  const dias = Number(cliente?.diasRestantesSubscricao ?? 0);

  if (Number(cliente?.subscricaoCongelada || 0) === 1) {
    return `${dias} dia${dias === 1 ? "" : "s"} congelado${dias === 1 ? "" : "s"}`;
  }

  return dias > 0 ? `${dias} dia${dias === 1 ? "" : "s"}` : "Expirada";
}

function estadoValidadeCliente(cliente) {
  if (Number(cliente?.subscricaoCongelada || 0) === 1) return "Congelada";
  if (clienteAtivo(cliente)) return "Activa";
  return "Desconectada";
}

const TIPOS_ADMIN = ["admin", "admin_pleno"];
const DEFAULT_ADMIN_LIMIT_DAYS = 5;
const MAX_CONFIGURABLE_ADMIN_DAYS = 3650;
const ADMIN_DEFAULT_PERMISSIONS = {
  verClientesAtivos: true,
  verClientesDesativos: true,
  verTodosPops: false,
  podeAtivarClientes: false,
  limiteDiasAtivacao: DEFAULT_ADMIN_LIMIT_DAYS,
};

function permissoesDoAdmin(admin = {}) {
  const permissoes = admin.permissoesAdmin || admin;
  const adminPleno = tipoDoUsuario(admin) === "admin_pleno";
  const limiteInformado = Number(
    permissoes.limiteDiasAtivacao || DEFAULT_ADMIN_LIMIT_DAYS
  );

  return {
    verClientesAtivos: Boolean(Number(permissoes.verClientesAtivos ?? 1)),
    verClientesDesativos: Boolean(Number(permissoes.verClientesDesativos ?? 1)),
    verTodosPops: Boolean(Number(permissoes.verTodosPops ?? 0)),
    podeAtivarClientes: adminPleno,
    limiteDiasAtivacao: Math.min(
      Math.max(
        Number.isFinite(limiteInformado)
          ? limiteInformado
          : DEFAULT_ADMIN_LIMIT_DAYS,
        1
      ),
      MAX_CONFIGURABLE_ADMIN_DAYS
    ),
  };
}

function montarPermissoesAdmin(tipoUsuario, permissoes = ADMIN_DEFAULT_PERMISSIONS) {
  const adminPleno = tipoUsuario === "admin_pleno";
  const limiteInformado = Number(
    permissoes.limiteDiasAtivacao || DEFAULT_ADMIN_LIMIT_DAYS
  );
  const permissoesNormalizadas = {
    ...permissoes,
    limiteDiasAtivacao: Math.min(
      Math.max(
        Number.isFinite(limiteInformado)
          ? limiteInformado
          : DEFAULT_ADMIN_LIMIT_DAYS,
        1
      ),
      MAX_CONFIGURABLE_ADMIN_DAYS
    ),
  };

  return {
    ...permissoesNormalizadas,
    podeAtivarClientes: adminPleno,
  };
}

function isTipoAdmin(tipo) {
  return ["admin", "admin_pleno", "super"].includes(String(tipo || ""));
}

export default function SuperAdmin() {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [recibos, setRecibos] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [relatoriosAtivacao, setRelatoriosAtivacao] = useState([]);
  const [logsSistema, setLogsSistema] = useState([]);
  const [mesRelatorio, setMesRelatorio] = useState(mesAtualFiltro());
  const [logsVisiveis, setLogsVisiveis] = useState(false);
  const [carregandoLogs, setCarregandoLogs] = useState(false);
  const [filtrosLogs, setFiltrosLogs] = useState({
    mes: mesAtualFiltro(),
    dia: "",
    hora: "",
    minuto: "",
    segundo: "",
    inicio: "",
    fim: "",
  });
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [idAlternando, setIdAlternando] = useState(null);
  const [clienteActionId, setClienteActionId] = useState(null);
  const [pagamentoActionId, setPagamentoActionId] = useState(null);
  const [pagamentosSemRecibo, setPagamentosSemRecibo] = useState([]);
  const [pagamentosVisiveis, setPagamentosVisiveis] = useState(false);
  const [carregandoPagamentos, setCarregandoPagamentos] = useState(false);
  const [filtroPagamentos, setFiltroPagamentos] = useState("");
  const [validadeClientesVisivel, setValidadeClientesVisivel] = useState(false);
  const [filtroClientesValidade, setFiltroClientesValidade] = useState("");
  const [filtro, setFiltro] = useState("");
  const [messageError, setMessageError] = useState("");
  const [novoAdmin, setNovoAdmin] = useState({
    name: "",
    password: "",
    pop: "",
    tipoUsuario: "admin",
    permissoesAdmin: montarPermissoesAdmin("admin"),
  });

  const token = localStorage.getItem("token");
  const headers = useMemo(() => (token ? authHeaders(token) : {}), [token]);

  const carregarDashboard = useCallback(async () => {
    try {
      setLoading(true);

      const [
        usuariosResp,
        adminsResp,
        recibosResp,
        planosResidenciaisResp,
        planosEmpresasResp,
        planosResidenciaisTriResp,
        planosEmpresasTriResp,
        relatoriosAtivacaoResp,
      ] = await Promise.allSettled([
        api.get("/usuarios", { headers }),
        api.get("/admins", { headers }),
        api.get("/recibos", { headers }),
        api.get("/planos", { headers }),
        api.get("/planosempresariais", { headers }),
        api.get("/planostrimestraisresidenciais", { headers }),
        api.get("/planostrimestraisempresariais", { headers }),
        api.get(`/relatorios/ativacoes?mes=${mesRelatorio}`, { headers }),
      ]);

      const todosUsuarios =
        usuariosResp.status === "fulfilled" && Array.isArray(usuariosResp.value.data)
          ? usuariosResp.value.data
          : [];
      const todosAdmins =
        adminsResp.status === "fulfilled" && Array.isArray(adminsResp.value.data)
          ? adminsResp.value.data
          : todosUsuarios.filter((user) => TIPOS_ADMIN.includes(tipoDoUsuario(user)));
      const todosRecibos =
        recibosResp.status === "fulfilled" && Array.isArray(recibosResp.value.data)
          ? recibosResp.value.data
          : [];
      const todosPlanos = [
        ...(planosResidenciaisResp.status === "fulfilled" &&
        Array.isArray(planosResidenciaisResp.value.data)
          ? planosResidenciaisResp.value.data
          : []),
        ...(planosEmpresasResp.status === "fulfilled" &&
        Array.isArray(planosEmpresasResp.value.data)
          ? planosEmpresasResp.value.data
          : []),
        ...(planosResidenciaisTriResp.status === "fulfilled" &&
        Array.isArray(planosResidenciaisTriResp.value.data)
          ? planosResidenciaisTriResp.value.data
          : []),
        ...(planosEmpresasTriResp.status === "fulfilled" &&
        Array.isArray(planosEmpresasTriResp.value.data)
          ? planosEmpresasTriResp.value.data
          : []),
      ];
      const todosRelatorios =
        relatoriosAtivacaoResp.status === "fulfilled" &&
        Array.isArray(relatoriosAtivacaoResp.value.data)
          ? relatoriosAtivacaoResp.value.data
          : [];

      setUsuarios(todosUsuarios);
      setAdmins(todosAdmins);
      setRecibos(todosRecibos);
      setPlanos(todosPlanos);
      setRelatoriosAtivacao(todosRelatorios);
    } catch (erro) {
      console.error("Erro ao carregar dashboard:", erro);
      Swal.fire("Erro", "Nao foi possivel carregar a dashboard.", "error");
    } finally {
      setLoading(false);
    }
  }, [headers, mesRelatorio]);

  const carregarLogs = useCallback(async () => {
    if (!logsVisiveis) return;

    try {
      setCarregandoLogs(true);
      const params = new URLSearchParams({ limit: "150" });

      Object.entries(filtrosLogs).forEach(([chave, valor]) => {
        if (valor !== undefined && valor !== null && String(valor).trim()) {
          params.set(chave, String(valor).trim());
        }
      });

      const resposta = await api.get(`/logs?${params.toString()}`, { headers });
      setLogsSistema(Array.isArray(resposta.data) ? resposta.data : []);
    } catch (erro) {
      console.error("Erro ao carregar logs:", erro);
      setLogsSistema([]);
      Swal.fire("Erro", "Nao foi possivel carregar os logs.", "error");
    } finally {
      setCarregandoLogs(false);
    }
  }, [headers, filtrosLogs, logsVisiveis]);

  const carregarPagamentosSemRecibo = useCallback(async () => {
    if (!pagamentosVisiveis) return;

    try {
      setCarregandoPagamentos(true);
      const resposta = await api.get("/pagamentos/sem-recibo", { headers });
      setPagamentosSemRecibo(Array.isArray(resposta.data) ? resposta.data : []);
    } catch (erro) {
      console.error("Erro ao carregar pagamentos sem recibo:", erro);
      setPagamentosSemRecibo([]);
      Swal.fire(
        "Erro",
        erro.response?.data?.mensagem ||
          "Nao foi possivel carregar pagamentos sem recibo.",
        "error"
      );
    } finally {
      setCarregandoPagamentos(false);
    }
  }, [headers, pagamentosVisiveis]);

  async function addAdmin() {
    try {
      setAdding(true);
      setMessageError("");

      if (!novoAdmin.name.trim() || !novoAdmin.password.trim()) {
        setMessageError("Nome e password sao obrigatorios.");
        return;
      }

      const permissoesAdmin = montarPermissoesAdmin(
        novoAdmin.tipoUsuario,
        novoAdmin.permissoesAdmin
      );

      await api.post(
        "/usuario",
        {
          name: novoAdmin.name.trim(),
          password: novoAdmin.password.trim(),
          tipoUsuario: novoAdmin.tipoUsuario,
          estado: "ativo",
          pop: novoAdmin.pop.trim() || null,
          permissoesAdmin,
        },
        { headers }
      );

      setNovoAdmin({
        name: "",
        password: "",
        pop: "",
        tipoUsuario: "admin",
        permissoesAdmin: montarPermissoesAdmin("admin"),
      });
      await carregarDashboard();
      Swal.fire("Criado", "Administrador criado com sucesso.", "success");
    } catch (erro) {
      console.error("Erro ao cadastrar admin:", erro);
      setMessageError("Houve algum erro ao cadastrar o admin.");
    } finally {
      setAdding(false);
    }
  }

  async function removeAdmin(id) {
    try {
      const resultado = await Swal.fire({
        title: "Remover administrador?",
        text: "Esta acao remove o acesso deste administrador.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#2563eb",
        confirmButtonText: "Sim, remover",
        cancelButtonText: "Cancelar",
      });

      if (!resultado.isConfirmed) return;

      setRemovingId(id);
      await api.delete(`/usuario/${id}`, { headers });
      await carregarDashboard();
      Swal.fire("Removido", "Administrador removido com sucesso.", "success");
    } catch (erro) {
      console.error(erro);
      Swal.fire("Erro", "Nao foi possivel remover o administrador.", "error");
    } finally {
      setRemovingId(null);
    }
  }

  async function alternarAtivo(adminUser) {
    try {
      setIdAlternando(adminUser.idUsuario);
      const novoEstado = adminUser.estado === "ativo" ? "desativo" : "ativo";

      await api.put(
        `/usuario/${adminUser.idUsuario}`,
        { estado: novoEstado },
        { headers }
      );

      await carregarDashboard();
    } catch (erro) {
      console.error("Erro ao alternar estado:", erro);
      Swal.fire("Erro", "Nao foi possivel alterar o estado.", "error");
    } finally {
      setIdAlternando(null);
    }
  }

  async function congelarValidade(cliente) {
    try {
      const dias = Number(cliente.diasRestantesSubscricao || 0);

      const resultado = await Swal.fire({
        title: "Congelar validade?",
        text: `O cliente ${cliente.name} ficara sem acesso e os ${dias} dia${
          dias === 1 ? "" : "s"
        } restantes serao guardados.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Congelar",
        cancelButtonText: "Cancelar",
      });

      if (!resultado.isConfirmed) return;

      setClienteActionId(cliente.idUsuario);
      await api.post(
        `/clientes/${cliente.idUsuario}/congelar-validade`,
        {},
        { headers }
      );
      await carregarDashboard();
      Swal.fire("Congelada", "Validade congelada com sucesso.", "success");
    } catch (erro) {
      Swal.fire(
        "Erro",
        erro.response?.data?.mensagem || "Nao foi possivel congelar a validade.",
        "error"
      );
    } finally {
      setClienteActionId(null);
    }
  }

  async function reativarValidade(cliente) {
    try {
      const resultado = await Swal.fire({
        title: "Reactivar validade?",
        text: `O cliente ${cliente.name} voltara a usar os dias congelados.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#16a34a",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Reactivar",
        cancelButtonText: "Cancelar",
      });

      if (!resultado.isConfirmed) return;

      setClienteActionId(cliente.idUsuario);
      await api.post(
        `/clientes/${cliente.idUsuario}/reativar-validade`,
        {},
        { headers }
      );
      await carregarDashboard();
      Swal.fire("Reactivada", "Validade reactivada com sucesso.", "success");
    } catch (erro) {
      Swal.fire(
        "Erro",
        erro.response?.data?.mensagem || "Nao foi possivel reactivar a validade.",
        "error"
      );
    } finally {
      setClienteActionId(null);
    }
  }

  async function desconectarCliente(cliente) {
    try {
      const resultado = await Swal.fire({
        title: "Desconectar cliente?",
        text: `O cliente ${cliente.name} perdera o acesso imediatamente.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Desconectar",
        cancelButtonText: "Cancelar",
      });

      if (!resultado.isConfirmed) return;

      setClienteActionId(cliente.idUsuario);
      await api.post(
        `/clientes/${cliente.idUsuario}/desconectar`,
        {},
        { headers }
      );
      await carregarDashboard();
      Swal.fire("Desconectado", "Cliente desconectado com sucesso.", "success");
    } catch (erro) {
      Swal.fire(
        "Erro",
        erro.response?.data?.mensagem || "Nao foi possivel desconectar o cliente.",
        "error"
      );
    } finally {
      setClienteActionId(null);
    }
  }

  async function gerarReciboPendente(pagamento) {
    try {
      const resultado = await Swal.fire({
        title: "Gerar recibo?",
        text: `Confirmar emissao de recibo para ${pagamento.userName || "cliente"} no valor de ${moeda(pagamento.valor)}.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#2563eb",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Gerar recibo",
        cancelButtonText: "Cancelar",
      });

      if (!resultado.isConfirmed) return;

      setPagamentoActionId(pagamento.idPagamento);
      const resposta = await api.post(
        `/pagamentos/${pagamento.idPagamento}/gerar-recibo`,
        {},
        { headers }
      );

      await carregarPagamentosSemRecibo();
      await carregarDashboard();
      Swal.fire(
        "Recibo gerado",
        `Recibo #${resposta.data?.reciboId || ""} gerado com sucesso.`,
        "success"
      );
    } catch (erro) {
      Swal.fire(
        "Erro",
        erro.response?.data?.mensagem || "Nao foi possivel gerar o recibo.",
        "error"
      );
    } finally {
      setPagamentoActionId(null);
    }
  }

  function atualizarPermissaoAdmin(chave, valor) {
    setNovoAdmin((atual) => ({
      ...atual,
      permissoesAdmin: montarPermissoesAdmin(atual.tipoUsuario, {
        ...atual.permissoesAdmin,
        [chave]: valor,
      }),
    }));
  }

  function atualizarTipoAdmin(tipoUsuario) {
    setNovoAdmin((atual) => ({
      ...atual,
      tipoUsuario,
      permissoesAdmin: montarPermissoesAdmin(
        tipoUsuario,
        atual.permissoesAdmin
      ),
    }));
  }

  useEffect(() => {
    carregarDashboard();
  }, [carregarDashboard]);

  useEffect(() => {
    carregarLogs();
  }, [carregarLogs]);

  useEffect(() => {
    carregarPagamentosSemRecibo();
  }, [carregarPagamentosSemRecibo]);

  const dados = useMemo(() => {
    const clientes = usuarios.filter((user) => {
      const tipo = tipoDoUsuario(user);
      return !isTipoAdmin(tipo) && tipo !== "colab";
    });
    const ativos = clientes.filter(clienteAtivo);
    const desativos = clientes.filter((user) => !clienteAtivo(user));
    const agora = new Date();
    const mesAtual = agora.getMonth();
    const anoAtual = agora.getFullYear();
    const recibosMes = recibos.filter((recibo) => {
      const data = new Date(recibo.dataTransacao);
      return data.getMonth() === mesAtual && data.getFullYear() === anoAtual;
    });
    const receitaTotal = recibos.reduce(
      (total, recibo) => total + Number(recibo.valor || 0),
      0
    );
    const receitaMes = recibosMes.reduce(
      (total, recibo) => total + Number(recibo.valor || 0),
      0
    );
    const porPop = clientes.reduce((acc, user) => {
      const pop = user.pop || "Sem POP";
      acc[pop] = (acc[pop] || 0) + 1;
      return acc;
    }, {});
    const topPops = Object.entries(porPop)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    const recibosRecentes = [...recibos]
      .sort((a, b) => new Date(b.dataTransacao) - new Date(a.dataTransacao))
      .slice(0, 6);
    const clientesRecentes = [...clientes].slice(-6).reverse();

    return {
      clientes,
      ativos,
      desativos,
      receitaTotal,
      receitaMes,
      recibosMes,
      topPops,
      recibosRecentes,
      clientesRecentes,
    };
  }, [usuarios, recibos]);

  const adminsFiltrados = admins.filter((admin) => {
    const termo = filtro.toLowerCase().trim();
    if (!termo) return true;
    return (
      String(admin.name || "").toLowerCase().includes(termo) ||
      String(admin.pop || "").toLowerCase().includes(termo) ||
      String(admin.estado || "").toLowerCase().includes(termo)
    );
  });

  const clientesValidadeFiltrados = dados.clientes.filter((cliente) => {
    const termo = filtroClientesValidade.toLowerCase().trim();
    if (!termo) return true;

    return (
      String(cliente.name || "").toLowerCase().includes(termo) ||
      String(cliente.pop || "").toLowerCase().includes(termo) ||
      String(cliente.profile || "").toLowerCase().includes(termo) ||
      String(cliente.profileusuario || "").toLowerCase().includes(termo) ||
      String(cliente.idUsuario || "").includes(termo)
    );
  });

  const pagamentosSemReciboFiltrados = pagamentosSemRecibo.filter((pagamento) => {
    const termo = filtroPagamentos.toLowerCase().trim();
    if (!termo) return true;

    return (
      String(pagamento.userName || "").toLowerCase().includes(termo) ||
      String(pagamento.numeroTelefone || "").toLowerCase().includes(termo) ||
      String(pagamento.plano || "").toLowerCase().includes(termo) ||
      String(pagamento.pop || "").toLowerCase().includes(termo) ||
      String(pagamento.referenciaTransacao || "").toLowerCase().includes(termo) ||
      String(pagamento.referenciaTerceiro || "").toLowerCase().includes(termo)
    );
  });

  const metricas = [
    {
      label: "Clientes",
      value: dados.clientes.length,
      detail: `${dados.ativos.length} activos`,
      icon: Users,
      tone: "bg-blue-50 text-blue-700",
    },
    {
      label: "Subscricoes activas",
      value: dados.ativos.length,
      detail: `${dados.desativos.length} terminadas`,
      icon: UserCheck,
      tone: "bg-emerald-50 text-emerald-700",
    },
    {
      label: "Receita do mes",
      value: moeda(dados.receitaMes),
      detail: `${dados.recibosMes.length} recibos`,
      icon: Wallet,
      tone: "bg-amber-50 text-amber-700",
    },
    {
      label: "Administradores",
      value: admins.length,
      detail: `${admins.filter((admin) => admin.estado === "ativo").length} activos`,
      icon: ShieldCheck,
      tone: "bg-indigo-50 text-indigo-700",
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-slate-100 font-questrial">
      <div className="bg-blue-600 shadow-md">
        <NavBar />
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loading />
        </div>
      ) : (
        <main className="mx-auto flex w-full max-w-7xl flex-1 flex-col gap-6 px-4 py-6">
          <section className="flex flex-col gap-4 rounded-md bg-white p-5 shadow-sm md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-blue-600">Super Admin</p>
              <h1 className="text-2xl font-bold text-slate-900">
                Dashboard geral
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Visao geral de clientes, pagamentos, POPs e administradores.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={carregarDashboard}
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                <RefreshCw size={17} />
                Actualizar
              </button>
              <button
                type="button"
                onClick={() => navigate("/admin")}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"
              >
                <UserCog size={17} />
                Area admin
              </button>
            </div>
          </section>

          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {metricas.map(({ label, value, detail, icon: Icon, tone }) => (
              <div
                key={label}
                className="rounded-md border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm text-slate-500">{label}</p>
                    <p className="mt-1 text-2xl font-bold text-slate-900">
                      {value}
                    </p>
                  </div>
                  <div className={`rounded-md p-3 ${tone}`}>
                    <Icon size={24} />
                  </div>
                </div>
                <p className="mt-3 text-sm font-semibold text-slate-500">
                  {detail}
                </p>
              </div>
            ))}
          </section>

          <section className="grid gap-6 lg:grid-cols-3">
            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm lg:col-span-2">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-slate-900">
                    Actividade recente
                  </h2>
                  <p className="text-sm text-slate-500">
                    Ultimos recibos emitidos no sistema.
                  </p>
                </div>
                <ReceiptText className="text-blue-600" size={22} />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px] text-left text-sm">
                  <thead className="border-b text-slate-500">
                    <tr>
                      <th className="py-2 font-semibold">Cliente</th>
                      <th className="py-2 font-semibold">Metodo</th>
                      <th className="py-2 font-semibold">Valor</th>
                      <th className="py-2 font-semibold">Data</th>
                      <th className="py-2 font-semibold">Recibo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.recibosRecentes.map((recibo) => (
                      <tr key={recibo.idRecibo} className="border-b last:border-0">
                        <td className="py-3 font-semibold text-slate-800">
                          {recibo.userName || "Sem nome"}
                        </td>
                        <td className="py-3 text-slate-600">
                          {recibo.meioPagamento || "N/D"}
                        </td>
                        <td className="py-3 text-slate-800">
                          {moeda(recibo.valor)}
                        </td>
                        <td className="py-3 text-slate-600">
                          {dataCurta(recibo.dataTransacao)}
                        </td>
                        <td className="py-3">
                          <button
                            type="button"
                            onClick={() => navigate(`/recibo/${recibo.idRecibo}`)}
                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-50"
                          >
                            Abrir
                            <ExternalLink size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {dados.recibosRecentes.length === 0 && (
                      <tr>
                        <td className="py-6 text-center text-slate-500" colSpan="5">
                          Sem recibos recentes.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-slate-900">Clientes por POP</h2>
                  <p className="text-sm text-slate-500">Maiores concentracoes.</p>
                </div>
                <RadioTower className="text-blue-600" size={22} />
              </div>

              <div className="space-y-3">
                {dados.topPops.map(([pop, total]) => (
                  <div key={pop}>
                    <div className="mb-1 flex justify-between text-sm">
                      <span className="font-semibold text-slate-700">{pop}</span>
                      <span className="text-slate-500">{total}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100">
                      <div
                        className="h-2 rounded-full bg-blue-600"
                        style={{
                          width: `${Math.max(
                            8,
                            (total / Math.max(dados.clientes.length, 1)) * 100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
                {dados.topPops.length === 0 && (
                  <p className="text-sm text-slate-500">Sem dados de POP.</p>
                )}
              </div>

              <div className="mt-6 rounded-md bg-slate-50 p-3">
                <p className="text-sm text-slate-500">Receita acumulada</p>
                <p className="mt-1 text-xl font-bold text-slate-900">
                  {moeda(dados.receitaTotal)}
                </p>
              </div>
            </div>
          </section>

          <section className="grid gap-6 lg:grid-cols-3">
            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="font-semibold text-slate-900">Acoes rapidas</h2>
              <p className="mt-1 text-sm text-slate-500">
                {planos.length} planos carregados para venda.
              </p>
              <div className="mt-4 grid gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/admin/clientes")}
                  className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3 text-left font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Ver clientes
                  <Users size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/admin/clientes/ativos")}
                  className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3 text-left font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Clientes activos
                  <UserCheck size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/admin/clientes/desativos")}
                  className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3 text-left font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Clientes desativos
                  <UserX size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/admin/broadcast")}
                  className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3 text-left font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Broadcast
                  <Wifi size={18} />
                </button>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="font-semibold text-slate-900">Criar administrador</h2>
              <div className="mt-4 space-y-3">
                {messageError && (
                  <div className="rounded-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
                    {messageError}
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Nome do admin"
                  value={novoAdmin.name}
                  onChange={(e) =>
                    setNovoAdmin((atual) => ({ ...atual, name: e.target.value }))
                  }
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={novoAdmin.password}
                  onChange={(e) =>
                    setNovoAdmin((atual) => ({
                      ...atual,
                      password: e.target.value,
                    }))
                  }
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  placeholder="POP associado"
                  value={novoAdmin.pop}
                  onChange={(e) =>
                    setNovoAdmin((atual) => ({ ...atual, pop: e.target.value }))
                  }
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
                <select
                  value={novoAdmin.tipoUsuario}
                  onChange={(e) => atualizarTipoAdmin(e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                >
                  <option value="admin">Admin normal</option>
                  <option value="admin_pleno">Admin pleno</option>
                </select>
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <p className="mb-2 text-sm font-semibold text-slate-800">
                    Privilegios
                  </p>
                  <div className="grid gap-2 text-sm text-slate-700">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={novoAdmin.permissoesAdmin.verClientesAtivos}
                        onChange={(e) =>
                          atualizarPermissaoAdmin(
                            "verClientesAtivos",
                            e.target.checked
                          )
                        }
                      />
                      Ver clientes activos
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={novoAdmin.permissoesAdmin.verClientesDesativos}
                        onChange={(e) =>
                          atualizarPermissaoAdmin(
                            "verClientesDesativos",
                            e.target.checked
                          )
                        }
                      />
                      Ver clientes desativos
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={novoAdmin.permissoesAdmin.verTodosPops}
                        onChange={(e) =>
                          atualizarPermissaoAdmin("verTodosPops", e.target.checked)
                        }
                      />
                      Ver todos os POPs
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={novoAdmin.permissoesAdmin.podeAtivarClientes}
                        disabled
                        onChange={(e) =>
                          atualizarPermissaoAdmin(
                            "podeAtivarClientes",
                            e.target.checked
                          )
                        }
                      />
                      Pode dar dias extras
                    </label>
                  </div>
                  <label className="mt-3 block text-sm font-semibold text-slate-700">
                    Limite de dias extras do admin pleno
                    <input
                      type="number"
                      min="1"
                      max={MAX_CONFIGURABLE_ADMIN_DAYS}
                      value={novoAdmin.permissoesAdmin.limiteDiasAtivacao}
                      disabled={novoAdmin.tipoUsuario !== "admin_pleno"}
                      onChange={(e) =>
                        atualizarPermissaoAdmin(
                          "limiteDiasAtivacao",
                          Number(e.target.value)
                        )
                      }
                      className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-500"
                    />
                  </label>
                  {novoAdmin.tipoUsuario === "admin_pleno" && (
                    <p className="mt-2 text-xs text-slate-500">
                      Admin pleno pode conceder dias extras ate ao limite definido aqui.
                    </p>
                  )}
                  {novoAdmin.tipoUsuario === "admin" && (
                    <p className="mt-2 text-xs text-slate-500">
                      Admin normal apenas visualiza clientes conforme os privilegios acima.
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={addAdmin}
                  disabled={adding}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {adding ? (
                    <>
                      <LoaderCircle className="animate-spin" size={17} />
                      Processando
                    </>
                  ) : (
                    <>
                      <Plus size={17} />
                      Adicionar admin
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-slate-900">
                    Clientes recentes
                  </h2>
                  <p className="text-sm text-slate-500">Ultimos registos locais.</p>
                </div>
                <BarChart3 className="text-blue-600" size={22} />
              </div>
              <div className="space-y-3">
                {dados.clientesRecentes.map((cliente) => (
                  <div
                    key={cliente.idUsuario}
                    className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2"
                  >
                    <div>
                      <p className="font-semibold text-slate-800">
                        {cliente.name || "Sem nome"}
                      </p>
                      <p className="text-xs text-slate-500">
                        {cliente.pop || "Sem POP"} - {cliente.profile || "Sem plano"}
                      </p>
                    </div>
                    <span
                      className={`rounded-md px-2 py-1 text-xs font-semibold ${
                        clienteAtivo(cliente)
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      {clienteAtivo(cliente) ? "Activo" : "Terminado"}
                    </span>
                  </div>
                ))}
                {dados.clientesRecentes.length === 0 && (
                  <p className="text-sm text-slate-500">Sem clientes recentes.</p>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="font-semibold text-slate-900">
                  Pagamentos sem recibo
                </h2>
                <p className="text-sm text-slate-500">
                  Verificacao reservada ao super admin para pagamentos confirmados no M-Pesa.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {pagamentosVisiveis && (
                  <button
                    type="button"
                    onClick={carregarPagamentosSemRecibo}
                    disabled={carregandoPagamentos}
                    className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  >
                    <RefreshCw size={16} />
                    {carregandoPagamentos ? "A carregar..." : "Actualizar"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setPagamentosVisiveis((actual) => !actual)}
                  className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
                >
                  <ReceiptText size={16} />
                  {pagamentosVisiveis ? "Ocultar pagamentos" : "Mostrar pagamentos"}
                </button>
              </div>
            </div>

            {pagamentosVisiveis && (
              <>
                <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="relative w-full md:w-80">
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      size={17}
                    />
                    <input
                      type="search"
                      placeholder="Pesquisar pagamento..."
                      value={filtroPagamentos}
                      onChange={(e) => setFiltroPagamentos(e.target.value)}
                      className="w-full rounded-md border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500"
                    />
                  </div>
                  <p className="text-sm text-slate-500">
                    {pagamentosSemReciboFiltrados.length} pendente(s)
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1080px] text-left text-sm">
                    <thead className="border-b text-slate-500">
                      <tr>
                        <th className="py-2 font-semibold">Cliente</th>
                        <th className="py-2 font-semibold">Telefone</th>
                        <th className="py-2 font-semibold">Valor</th>
                        <th className="py-2 font-semibold">Plano</th>
                        <th className="py-2 font-semibold">Referencia</th>
                        <th className="py-2 font-semibold">Confirmacao</th>
                        <th className="py-2 font-semibold">Data</th>
                        <th className="py-2 text-right font-semibold">Acao</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagamentosSemReciboFiltrados.map((pagamento) => {
                        const processando =
                          pagamentoActionId === pagamento.idPagamento;

                        return (
                          <tr
                            key={pagamento.idPagamento}
                            className="border-b last:border-0"
                          >
                            <td className="py-3">
                              <p className="font-semibold text-slate-800">
                                {pagamento.userName || "Sem nome"}
                              </p>
                              <p className="text-xs text-slate-500">
                                {pagamento.pop || "Sem POP"} - ID {pagamento.idUsuario}
                              </p>
                            </td>
                            <td className="py-3 text-slate-600">
                              {pagamento.numeroTelefone || "N/D"}
                            </td>
                            <td className="py-3 font-semibold text-slate-800">
                              {moeda(pagamento.valor)}
                            </td>
                            <td className="max-w-[190px] py-3 text-slate-600">
                              {pagamento.plano || "Sem plano"}
                            </td>
                            <td className="py-3 text-xs text-slate-600">
                              <p>{pagamento.referenciaTransacao || "Sem referencia"}</p>
                              <p className="text-slate-400">
                                {pagamento.referenciaTerceiro || "Sem codigo"}
                              </p>
                            </td>
                            <td className="py-3">
                              <span className="rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">
                                {pagamento.responseCode || "Confirmado"}
                              </span>
                              <p className="mt-1 max-w-[220px] text-xs text-slate-500">
                                {pagamento.responseDesc || "Pagamento confirmado"}
                              </p>
                            </td>
                            <td className="py-3 text-slate-600">
                              {dataHoraCurta(pagamento.dataTransacao)}
                            </td>
                            <td className="py-3">
                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => gerarReciboPendente(pagamento)}
                                  disabled={processando}
                                  className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-500 disabled:opacity-60"
                                >
                                  <ReceiptText size={14} />
                                  {processando ? "A gerar..." : "Gerar recibo"}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {pagamentosSemReciboFiltrados.length === 0 && (
                        <tr>
                          <td className="py-6 text-center text-slate-500" colSpan="8">
                            Nenhum pagamento sem recibo encontrado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="font-semibold text-slate-900">
                  Validade dos clientes
                </h2>
                <p className="text-sm text-slate-500">
                  Dias restantes, congelamento e desconexao imediata ficam ocultos ate abrir.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {validadeClientesVisivel && (
                  <button
                    type="button"
                    onClick={carregarDashboard}
                    className="inline-flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <RefreshCw size={16} />
                    Actualizar
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setValidadeClientesVisivel((actual) => !actual)}
                  className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
                >
                  <Power size={16} />
                  {validadeClientesVisivel ? "Ocultar clientes" : "Mostrar clientes"}
                </button>
              </div>
            </div>

            {validadeClientesVisivel && (
              <>
                <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div className="relative w-full md:w-80">
                    <Search
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      size={17}
                    />
                    <input
                      type="search"
                      placeholder="Pesquisar cliente..."
                      value={filtroClientesValidade}
                      onChange={(e) => setFiltroClientesValidade(e.target.value)}
                      className="w-full rounded-md border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500"
                    />
                  </div>
                  <p className="text-sm text-slate-500">
                    {clientesValidadeFiltrados.length} cliente(s)
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[980px] text-left text-sm">
                    <thead className="border-b text-slate-500">
                      <tr>
                        <th className="py-2 font-semibold">Cliente</th>
                        <th className="py-2 font-semibold">POP</th>
                        <th className="py-2 font-semibold">Plano</th>
                        <th className="py-2 font-semibold">Estado</th>
                        <th className="py-2 font-semibold">Dias restantes</th>
                        <th className="py-2 text-right font-semibold">Acoes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientesValidadeFiltrados.map((cliente) => {
                        const congelada =
                          Number(cliente.subscricaoCongelada || 0) === 1;
                        const processando =
                          clienteActionId === cliente.idUsuario;

                        return (
                          <tr key={cliente.idUsuario} className="border-b last:border-0">
                            <td className="py-3 font-semibold text-slate-800">
                              {cliente.name || "Sem nome"}
                            </td>
                            <td className="py-3 text-slate-600">
                              {cliente.pop || "Sem POP"}
                            </td>
                            <td className="py-3 text-slate-600">
                              {cliente.profileusuario || cliente.profile || "Sem plano"}
                            </td>
                            <td className="py-3">
                              <span
                                className={`rounded-md px-2 py-1 text-xs font-semibold ${
                                  congelada
                                    ? "bg-amber-50 text-amber-700"
                                    : clienteAtivo(cliente)
                                      ? "bg-emerald-50 text-emerald-700"
                                      : "bg-red-50 text-red-700"
                                }`}
                              >
                                {estadoValidadeCliente(cliente)}
                              </span>
                            </td>
                            <td className="py-3 text-slate-700">
                              {diasRestantesLabel(cliente)}
                            </td>
                            <td className="py-3">
                              <div className="flex flex-wrap justify-end gap-2">
                                {congelada ? (
                                  <button
                                    type="button"
                                    onClick={() => reativarValidade(cliente)}
                                    disabled={processando}
                                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                                  >
                                    <PlayCircle size={14} />
                                    Reactivar
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => congelarValidade(cliente)}
                                    disabled={
                                      processando ||
                                      Number(cliente.diasRestantesSubscricao || 0) < 1
                                    }
                                    className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-3 py-1 text-xs font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
                                  >
                                    <PauseCircle size={14} />
                                    Congelar
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => desconectarCliente(cliente)}
                                  disabled={processando}
                                  className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                                >
                                  <Power size={14} />
                                  Desconectar
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {clientesValidadeFiltrados.length === 0 && (
                        <tr>
                          <td className="py-6 text-center text-slate-500" colSpan="6">
                            Nenhum cliente encontrado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="font-semibold text-slate-900">Logs do sistema</h2>
                <p className="text-sm text-slate-500">
                  Abra apenas quando precisar auditar as accoes feitas no sistema.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Activity className="text-blue-600" size={22} />
                <button
                  type="button"
                  onClick={() => setLogsVisiveis((actual) => !actual)}
                  className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
                >
                  {logsVisiveis ? "Esconder logs" : "Mostrar logs"}
                </button>
              </div>
            </div>

            {logsVisiveis && (
              <>
                <div className="mb-4 grid gap-3 md:grid-cols-3 xl:grid-cols-7">
                  <label className="text-xs font-semibold text-slate-600">
                    Mes
                    <input
                      type="month"
                      value={filtrosLogs.mes}
                      onChange={(e) =>
                        setFiltrosLogs((actual) => ({
                          ...actual,
                          mes: e.target.value,
                        }))
                      }
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Dia
                    <input
                      type="date"
                      value={filtrosLogs.dia}
                      onChange={(e) =>
                        setFiltrosLogs((actual) => ({
                          ...actual,
                          dia: e.target.value,
                        }))
                      }
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Hora
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={filtrosLogs.hora}
                      onChange={(e) =>
                        setFiltrosLogs((actual) => ({
                          ...actual,
                          hora: e.target.value,
                        }))
                      }
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Minuto
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={filtrosLogs.minuto}
                      onChange={(e) =>
                        setFiltrosLogs((actual) => ({
                          ...actual,
                          minuto: e.target.value,
                        }))
                      }
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Segundo
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={filtrosLogs.segundo}
                      onChange={(e) =>
                        setFiltrosLogs((actual) => ({
                          ...actual,
                          segundo: e.target.value,
                        }))
                      }
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Inicio
                    <input
                      type="datetime-local"
                      step="1"
                      value={filtrosLogs.inicio}
                      onChange={(e) =>
                        setFiltrosLogs((actual) => ({
                          ...actual,
                          inicio: e.target.value,
                        }))
                      }
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </label>
                  <label className="text-xs font-semibold text-slate-600">
                    Fim
                    <input
                      type="datetime-local"
                      step="1"
                      value={filtrosLogs.fim}
                      onChange={(e) =>
                        setFiltrosLogs((actual) => ({
                          ...actual,
                          fim: e.target.value,
                        }))
                      }
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                    />
                  </label>
                </div>

                <div className="mb-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={carregarLogs}
                    disabled={carregandoLogs}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-60"
                  >
                    {carregandoLogs ? "A carregar..." : "Aplicar filtros"}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setFiltrosLogs({
                        mes: mesAtualFiltro(),
                        dia: "",
                        hora: "",
                        minuto: "",
                        segundo: "",
                        inicio: "",
                        fim: "",
                      })
                    }
                    className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Limpar filtros
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full min-w-[920px] text-left text-sm">
                    <thead className="border-b text-slate-500">
                      <tr>
                        <th className="py-2 font-semibold">Quando</th>
                        <th className="py-2 font-semibold">Quem fez</th>
                        <th className="py-2 font-semibold">Cliente</th>
                        <th className="py-2 font-semibold">O que aconteceu</th>
                        <th className="py-2 font-semibold">Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logsSistema.map((log) => (
                        <tr key={log.idLog} className="border-b last:border-0">
                          <td className="py-3 text-slate-600">
                            {dataHoraCurta(log.criadoEm || log.createdAt)}
                          </td>
                          <td className="py-3 font-semibold text-slate-800">
                            {log.nomeUsuario || "Sistema"}
                          </td>
                          <td className="py-3 text-slate-600">
                            {clienteDoLog(log)}
                          </td>
                          <td className="max-w-[440px] py-3 text-slate-700">
                            {resumoLog(log)}
                          </td>
                          <td className="py-3">
                            <span
                              className={`rounded-md px-2 py-1 text-xs font-semibold ${
                                Number(log.sucesso) === 1
                                  ? "bg-emerald-50 text-emerald-700"
                                  : "bg-red-50 text-red-700"
                              }`}
                            >
                              {resultadoDoLog(log)}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {logsSistema.length === 0 && (
                        <tr>
                          <td className="py-6 text-center text-slate-500" colSpan="5">
                            Nenhum log encontrado para estes filtros.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="font-semibold text-slate-900">
                  Relatorio de activacoes
                </h2>
                <p className="text-sm text-slate-500">
                  Dias extras concedidos por admins, com motivo obrigatorio.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold text-slate-600">
                  Mes
                  <input
                    type="month"
                    value={mesRelatorio}
                    onChange={(e) => setMesRelatorio(e.target.value)}
                    className="ml-2 rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500"
                  />
                </label>
                <ShieldCheck className="text-blue-600" size={22} />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-left text-sm">
                <thead className="border-b text-slate-500">
                  <tr>
                    <th className="py-2 font-semibold">Admin</th>
                    <th className="py-2 font-semibold">Cliente</th>
                    <th className="py-2 font-semibold">POP</th>
                    <th className="py-2 font-semibold">Dias</th>
                    <th className="py-2 font-semibold">Uso</th>
                    <th className="py-2 font-semibold">Observacao</th>
                    <th className="py-2 font-semibold">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {relatoriosAtivacao.map((relatorio) => (
                    <tr
                      key={relatorio.idAtivacao}
                      className="border-b last:border-0"
                    >
                      <td className="py-3 font-semibold text-slate-800">
                        {relatorio.adminName || relatorio.idAdmin}
                      </td>
                      <td className="py-3 text-slate-700">
                        {relatorio.clienteName || relatorio.idCliente}
                      </td>
                      <td className="py-3 text-slate-600">
                        {relatorio.popCliente || "Sem POP"}
                      </td>
                      <td className="py-3 text-slate-700">
                        {relatorio.diasConcedidos} dias
                      </td>
                      <td className="py-3 text-slate-600">
                        {Number(relatorio.autorizadoPorSuper) === 1
                          ? "Sem limite"
                          : `${relatorio.diasUsadosDepois}/${relatorio.limiteDias}`}
                      </td>
                      <td className="max-w-[260px] py-3 text-slate-600">
                        {relatorio.observacao}
                      </td>
                      <td className="py-3 text-slate-600">
                        {dataCurta(relatorio.dataAtivacao)}
                      </td>
                    </tr>
                  ))}
                  {relatoriosAtivacao.length === 0 && (
                    <tr>
                      <td className="py-6 text-center text-slate-500" colSpan="7">
                        Nenhuma activacao extra registada.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="font-semibold text-slate-900">
                  Gestao de administradores
                </h2>
                <p className="text-sm text-slate-500">
                  Activar, desativar ou remover administradores por POP.
                </p>
              </div>
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={17}
                />
                <input
                  type="text"
                  placeholder="Pesquisar admin..."
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  className="w-full rounded-md border border-slate-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 md:w-72"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-left text-sm">
                <thead className="border-b text-slate-500">
                  <tr>
                    <th className="py-2 font-semibold">Nome</th>
                    <th className="py-2 font-semibold">Tipo</th>
                    <th className="py-2 font-semibold">POP</th>
                    <th className="py-2 font-semibold">Privilegios</th>
                    <th className="py-2 font-semibold">Estado</th>
                    <th className="py-2 font-semibold">ID</th>
                    <th className="py-2 text-right font-semibold">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {adminsFiltrados.map((adminUser) => (
                    <tr
                      key={adminUser.idUsuario}
                      className="border-b last:border-0"
                    >
                      <td className="py-3 font-semibold text-slate-800">
                        {adminUser.name}
                      </td>
                      <td className="py-3 text-slate-600">
                        {tipoDoUsuario(adminUser) === "admin_pleno"
                          ? "Admin pleno"
                          : "Admin normal"}
                      </td>
                      <td className="py-3 text-slate-600">
                        {adminUser.pop || "Sem POP"}
                      </td>
                      <td className="py-3 text-xs text-slate-600">
                        <div className="flex flex-wrap gap-1">
                          {permissoesDoAdmin(adminUser).verClientesAtivos && (
                            <span className="rounded-md bg-emerald-50 px-2 py-1 text-emerald-700">
                              Ativos
                            </span>
                          )}
                          {permissoesDoAdmin(adminUser).verClientesDesativos && (
                            <span className="rounded-md bg-red-50 px-2 py-1 text-red-700">
                              Desativos
                            </span>
                          )}
                          <span className="rounded-md bg-blue-50 px-2 py-1 text-blue-700">
                            {permissoesDoAdmin(adminUser).verTodosPops
                              ? "Todos POPs"
                              : "POP proprio"}
                          </span>
                          {permissoesDoAdmin(adminUser).podeAtivarClientes && (
                            <span className="rounded-md bg-amber-50 px-2 py-1 text-amber-700">
                              {permissoesDoAdmin(adminUser).limiteDiasAtivacao} dias
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3">
                        <span
                          className={`rounded-md px-2 py-1 text-xs font-semibold ${
                            adminUser.estado === "ativo"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-red-50 text-red-700"
                          }`}
                        >
                          {adminUser.estado === "ativo" ? "Activo" : "Desativo"}
                        </span>
                      </td>
                      <td className="py-3 text-slate-500">
                        {adminUser.idUsuario}
                      </td>
                      <td className="py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => alternarAtivo(adminUser)}
                            disabled={idAlternando === adminUser.idUsuario}
                            className={`rounded-md px-3 py-1 text-xs font-semibold text-white disabled:opacity-70 ${
                              adminUser.estado === "ativo"
                                ? "bg-amber-500 hover:bg-amber-600"
                                : "bg-emerald-600 hover:bg-emerald-500"
                            }`}
                          >
                            {idAlternando === adminUser.idUsuario
                              ? "Aguarde..."
                              : adminUser.estado === "ativo"
                                ? "Desativar"
                                : "Activar"}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeAdmin(adminUser.idUsuario)}
                            disabled={removingId === adminUser.idUsuario}
                            className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-70"
                          >
                            {removingId === adminUser.idUsuario ? (
                              "Removendo..."
                            ) : (
                              <>
                                <Trash2 size={13} />
                                Remover
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {adminsFiltrados.length === 0 && (
                    <tr>
                      <td className="py-6 text-center text-slate-500" colSpan="7">
                        Nenhum administrador encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      )}

      <div className="bg-blue-600">
        <Footer />
      </div>
    </div>
  );
}
